import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useRegle } from '@regle/core';
import { maxLength, withMessage } from '@regle/rules';
import { captureConvenienceException } from '@client/monolith/core/plugins/sentry/helper';
import Analytics from '@monolith/legacy/services/analytics';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import { Mutation } from '@monolith/legacy/store/mutation-types';
import { BusinessType } from '../types/german-bi.types';
import type { BusinessTypeOption, GermanBiSubmitPayload, RegistrySearchResult, HqAddress } from '../types/german-bi.types';
import { submitGermanBi } from '../services/german-bi.service';

const DE_TAX_NUMBER_REGEX = /^((GnR)|(GNR)|(HRA)|(HRB)|(PR)|(VR))?[0-9]{1,6}[A-Z]{0,5}$/;

export function validDeTaxNumber(value: string): boolean {
  const normalized = value.replace(/\s/g, '').toUpperCase();
  return DE_TAX_NUMBER_REGEX.test(normalized);
}

const COMPONENT_MODAL = 'german_bi_modal';
const COMPONENT_HANDELSREGISTER = 'german_bi_handelsregister';
const COMPONENT_SOLE_TRADER = 'german_bi_sole_trader';

function trackEvent(component: string, action: string, extra: Record<string, unknown> = {}): void {
  Analytics.track(
    new UserClick({
      component,
      action,
      shouldNameIncludeComponent: true,
      ...extra,
    })
  );
}

function componentForType(type: BusinessType): string {
  return type === BusinessType.HasHandelsregister ? COMPONENT_HANDELSREGISTER : COMPONENT_SOLE_TRADER;
}

export function trackModalClosed(): void {
  trackEvent(COMPONENT_MODAL, 'modal_closed');
}

export function trackModalViewed(routePath: string): void {
  trackEvent(COMPONENT_MODAL, 'modal_viewed', { page: routePath });
}

export function trackSearchPerformed(query: string): void {
  trackEvent(COMPONENT_HANDELSREGISTER, 'registry_search_performed', { query });
}

export function trackResultSelected(companyName: string, regNumber: string): void {
  trackEvent(COMPONENT_HANDELSREGISTER, 'registry_search_result_selected', {
    company_name: companyName,
    registry_number: regNumber,
  });
}

function trackSelectionCleared(): void {
  trackEvent(COMPONENT_HANDELSREGISTER, 'registry_selection_cleared');
}

export function trackNoResults(query: string): void {
  trackEvent(COMPONENT_HANDELSREGISTER, 'registry_no_results', { query });
}

function trackSoleTraderNumberEntered(hasValue: boolean): void {
  trackEvent(COMPONENT_SOLE_TRADER, 'sole_trader_number_entered', { has_value: hasValue });
}

interface Country {
  id: number;
  iso_code: string;
  name?: string;
}

export function resolveCountryByIsoCode(
  countries: Country[],
  isoCode: string
): { countryId: number; countryIsoCode: string } | null {
  const country = countries.find((c) => c.iso_code === isoCode);
  if (!country) {
    return null;
  }
  return { countryId: country.id, countryIsoCode: country.iso_code };
}

export function buildStoreBusinessUpdate(
  currentBusiness: Record<string, unknown>,
  payload: GermanBiSubmitPayload,
  countries: Country[]
): Record<string, unknown> {
  const isSoleTrader = payload.type === BusinessType.SoleTrader;
  const updatedBusiness: Record<string, unknown> = {
    ...currentBusiness,
    is_sole_trader: isSoleTrader,
    tax_number: isSoleTrader ? '' : payload.registryNumber || '',
    sole_trader_number: isSoleTrader ? payload.soleTraderNumber || '' : '',
  };

  if (payload.hqAddress) {
    const country = countries.find((c) => c.id === payload.hqAddress!.countryId);
    updatedBusiness.headquarter_address = {
      type: 'headquarter_address',
      street: payload.hqAddress.street,
      postal_code: payload.hqAddress.postalCode,
      city: payload.hqAddress.city,
      country: country ? { iso_code: country.iso_code, name: country.name } : null,
    };
  }

  return updatedBusiness;
}

export function useGermanBi() {
  const store = useStore();
  const { t } = useI18n();
  const route = useRoute();

  const selectedType = ref<BusinessType>(BusinessType.HasHandelsregister);
  const registryNumber = ref<string>('');
  const soleTraderNumber = ref<string>('');
  const isSubmitting = ref(false);
  const submitError = ref<string | null>(null);

  const useItAnyway = ref(false);
  const selectedRegistryResult = ref<RegistrySearchResult | null>(null);
  const manualHqAddress = ref<HqAddress | null>(null);

  const business = computed(() => store.getters['account/business/getBusiness']);
  const retailer = computed(() => store.getters.retailer);

  const countries = computed(() => store.getters['global/countries/getAlphaOrderedCountries'] || []);

  const businessTypeOptions = computed<BusinessTypeOption[]>(() => [
    {
      value: BusinessType.HasHandelsregister,
      label: t('retailer.germanBusinessIdentifier.options.hasHandelsregister'),
    },
    {
      value: BusinessType.SoleTrader,
      label: t('retailer.germanBusinessIdentifier.options.soleTrader'),
    },
  ]);

  const { r$: registryValidation } = useRegle(
    { registryNumber },
    {
      registryNumber: {
        validDeTaxNumber: withMessage(validDeTaxNumber, () =>
          t('retailer.germanBusinessIdentifier.validation.invalidRegistryNumber')
        ),
      },
    },
    { lazy: true }
  );

  const { r$: soleTraderValidation } = useRegle(
    { soleTraderNumber },
    {
      soleTraderNumber: {
        maxLength: maxLength(255),
      },
    },
    { lazy: true }
  );

  const isRegistryNumberValid = computed(() => {
    if (registryNumber.value.trim().length === 0) {
      return false;
    }
    return !registryValidation.$fields.registryNumber.$invalid;
  });

  const isSoleTraderNumberValid = computed(() => {
    if (soleTraderNumber.value.trim().length === 0) {
      return true;
    }
    return !soleTraderValidation.$fields.soleTraderNumber.$invalid;
  });

  const canSubmit = computed(() => {
    if (selectedType.value === BusinessType.SoleTrader) {
      return isSoleTraderNumberValid.value;
    }
    if (useItAnyway.value) {
      return isRegistryNumberValid.value && manualHqAddress.value !== null;
    }
    return isRegistryNumberValid.value && selectedRegistryResult.value !== null;
  });

  const shouldShowModal = computed(() => !!retailer.value?.should_show_german_bi_modal);

  function resolveCountryId(isoCode: string): { countryId: number; countryIsoCode: string } | null {
    return resolveCountryByIsoCode(countries.value, isoCode);
  }

  function handleTypeChange(type: BusinessType): void {
    selectedType.value = type;
    submitError.value = null;
    useItAnyway.value = false;
    selectedRegistryResult.value = null;
    manualHqAddress.value = null;
    trackEvent(componentForType(type), 'option_selected', { business_type: type });
  }

  function handleRegistrySelect(result: RegistrySearchResult): void {
    selectedRegistryResult.value = result;
    useItAnyway.value = false;
  }

  function handleUseItAnyway(): void {
    useItAnyway.value = true;
    selectedRegistryResult.value = null;
    trackEvent(COMPONENT_HANDELSREGISTER, 'use_it_anyway_clicked');
  }

  function handleManualHqAddressUpdate(address: HqAddress | null): void {
    manualHqAddress.value = address;
  }

  function buildSubmitPayload(): GermanBiSubmitPayload {
    const payload: GermanBiSubmitPayload = { type: selectedType.value };

    if (selectedType.value === BusinessType.HasHandelsregister) {
      payload.registryNumber = registryNumber.value.trim();

      if (useItAnyway.value && manualHqAddress.value) {
        payload.hqAddress = manualHqAddress.value;
      } else if (selectedRegistryResult.value?.hqAddress) {
        const addr = selectedRegistryResult.value.hqAddress;
        const resolved = resolveCountryId(addr.countryCode);
        if (resolved) {
          payload.hqAddress = {
            street: addr.street,
            city: addr.city,
            postalCode: addr.postalCode,
            countryId: resolved.countryId,
            countryIsoCode: resolved.countryIsoCode,
          };
        }
      }
    } else if (selectedType.value === BusinessType.SoleTrader) {
      payload.soleTraderNumber = soleTraderNumber.value.trim() || undefined;
    }

    return payload;
  }

  function updateBusinessInStore(payload: GermanBiSubmitPayload): void {
    const updatedBusiness = buildStoreBusinessUpdate(business.value, payload, countries.value);
    store.commit(Mutation.UPDATE_BUSINESS, { business: updatedBusiness });
  }

  async function handleSubmit(): Promise<boolean> {
    if (selectedType.value === BusinessType.HasHandelsregister) {
      registryValidation.$touch();
    }

    if (!canSubmit.value) {
      return false;
    }

    isSubmitting.value = true;
    submitError.value = null;

    const component = componentForType(selectedType.value);

    try {
      const payload = buildSubmitPayload();
      await submitGermanBi(payload);
      updateBusinessInStore(payload);
      trackEvent(component, 'submit_success', { business_type: selectedType.value, use_it_anyway: useItAnyway.value });
      return true;
    } catch (error) {
      captureConvenienceException(error);
      submitError.value = t('retailer.germanBusinessIdentifier.modal.submitError');
      trackEvent(component, 'submit_error', {
        business_type: selectedType.value,
        use_it_anyway: useItAnyway.value,
        error: String(error),
      });
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  function reset(): void {
    selectedType.value = BusinessType.HasHandelsregister;
    registryNumber.value = '';
    soleTraderNumber.value = '';
    isSubmitting.value = false;
    submitError.value = null;
    registryValidation.$reset();
    soleTraderValidation.$fields.soleTraderNumber.$reset();
    useItAnyway.value = false;
    selectedRegistryResult.value = null;
    manualHqAddress.value = null;
  }

  return {
    selectedType,
    registryNumber,
    soleTraderNumber,
    isSubmitting,
    submitError,
    businessTypeOptions,
    canSubmit,
    isRegistryNumberValid,
    isSoleTraderNumberValid,
    shouldShowModal,
    registryValidation: registryValidation.$fields.registryNumber,
    useItAnyway,
    selectedRegistryResult,
    manualHqAddress,
    countries,
    soleTraderValidation: soleTraderValidation.$fields.soleTraderNumber,
    handleTypeChange,
    handleRegistrySelect,
    handleUseItAnyway,
    handleManualHqAddressUpdate,
    handleSubmit,
    buildSubmitPayload,
    reset,
    trackModalViewed: () => trackModalViewed(route.path),
    trackModalClosed,
    trackSearchPerformed,
    trackResultSelected,
    trackSelectionCleared,
    trackNoResults,
    trackSoleTraderNumberEntered,
  };
}
